/**
 * Per-page landing wrappers.
 *
 * Each variant has a dedicated wrapper file in this folder. By default
 * each wrapper is a one-liner that renders <LandingNew variant="..." />,
 * inheriting hero copy, sections, footer, navbar, styles and rotation
 * behavior from the shared template. To diverge a single page (custom
 * widgets, modals, analytics, hero overrides), edit only that wrapper —
 * other landing pages are unaffected.
 *
 * `LANDING_WRAPPERS` is the variant→component map App.tsx routes from.
 * Each wrapper is lazy-loaded so a visitor to /scanner only downloads
 * ScannerLanding's chunk (which transitively pulls in <LandingNew /> on
 * first hit, then caches it for sibling pages).
 *
 * Home page `/` and TAAFT routes do NOT use this map — they retain
 * their existing routing in App.tsx so this addition is purely additive
 * for the new feature landing pages.
 */
import { lazy } from "react";
import type { FeatureVariant } from "@/marketing/components/landing-new/heroVariants";

const ScannerLanding = lazy(
  () => import(/* webpackChunkName: "scanner-landing" */ "@/marketing/pages/landings/ScannerLanding")
);
const AlertsLanding = lazy(
  () => import(/* webpackChunkName: "alerts-landing" */ "@/marketing/pages/landings/AlertsLanding")
);
const BacktestingLanding = lazy(
  () =>
    import(
      /* webpackChunkName: "backtesting-landing" */ "@/marketing/pages/landings/BacktestingLanding"
    )
);
const WatchlistLanding = lazy(
  () =>
    import(/* webpackChunkName: "watchlist-landing" */ "@/marketing/pages/landings/WatchlistLanding")
);
const ByoaiLanding = lazy(
  () => import(/* webpackChunkName: "byoai-landing" */ "@/marketing/pages/landings/ByoaiLanding")
);
const PortfolioLanding = lazy(
  () =>
    import(/* webpackChunkName: "portfolio-landing" */ "@/marketing/pages/landings/PortfolioLanding")
);
const ResearchLanding = lazy(
  () => import(/* webpackChunkName: "research-landing" */ "@/marketing/pages/landings/ResearchLanding")
);
const KsaLanding = lazy(
  () => import(/* webpackChunkName: "ksa-landing" */ "@/marketing/pages/landings/KsaLanding")
);

export const LANDING_WRAPPERS: Record<
  FeatureVariant,
  React.LazyExoticComponent<React.ComponentType>
> = {
  scanner: ScannerLanding,
  alerts: AlertsLanding,
  backtesting: BacktestingLanding,
  watchlist: WatchlistLanding,
  byoai: ByoaiLanding,
  portfolio: PortfolioLanding,
  research: ResearchLanding,
  ksa: KsaLanding,
};
